import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  Tab,
  Tabs,
  Chip,
  Autocomplete,
  IconButton,
  CircularProgress,
  DialogActions,
  DialogTitle,
  DialogContentText,
} from '@mui/material';
import { X, Send, Sparkles, Save } from 'lucide-react';
import { AudienceType } from '../../types/communication';
import {
  AUDIENCE_GROUPS,
  VALIDATION_RULES,
  ANNOUNCEMENT_TEMPLATES,
} from '../../constants/communication';
import { EmergencyPopupToggle } from './EmergencyPopupToggle';
import { PopupPreview } from './PopupPreview';
import { useCreateAnnouncement } from '../../hooks/useCommunication';
import { useClasses } from '../../hooks/useClasses';
import { useStudents } from '../../hooks/useStudents';
import { useEmployees } from '../../hooks/useEmployees';
import { roleService } from '../../services/api/roles';
import { useAuthStore } from '../../store/authstore';
import { useQuery } from '@tanstack/react-query';
import {
  saveAudiencePreference,
  getAudiencePreference,
  clearAudiencePreference,
  saveAutoDraft,
  getAutoDraft,
  clearAutoDraft,
  hasAutoDraft,
  saveDraft,
  getSavedDrafts,
} from '../../utils/announcementDrafts';
import toast from 'react-hot-toast';

interface AnnouncementModalProps {
  open: boolean;
  onClose: () => void;
}

type TabValue = 'EVERYONE' | 'STUDENTS' | 'EMPLOYEES';

export const AnnouncementModal: React.FC<AnnouncementModalProps> = ({
  open,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<TabValue>('EVERYONE');
  const [audienceType, setAudienceType] = useState<AudienceType | ''>('');
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [showAsPopup, setShowAsPopup] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showRestoreBanner, setShowRestoreBanner] = useState(false);
  const [showSaveDraftDialog, setShowSaveDraftDialog] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const { mutate: createAnnouncement, isPending } = useCreateAnnouncement();

  // Get instituteId from auth
  const { session } = useAuthStore();
  const instituteId = session!.user.instituteId;

  // Fetch data for audience selection using existing hooks
  const { classes = [], isLoading: classesLoading } = useClasses(instituteId);
  
  const { useFetchStudents } = useStudents(instituteId);
  const { data: studentsData } = useFetchStudents({ instituteId, limit: 1000, page: 1 });
  const students = studentsData?.data || [];
  
  const { employees = [] } = useEmployees(instituteId);

  // Fetch roles
  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: () => roleService.getEmployeeRoles()
  });

  // Check if form has unsaved changes
  const hasUnsavedChanges = title.trim() !== '' || body.trim() !== '' || audienceType !== '';

  // Calculate recipient count - simplified (no complex calculations)
  const getRecipientCount = useCallback((): number => {
    if (!audienceType) return 0;
    // Just return 1 to indicate audience is selected, don't show actual count
    return 1;
  }, [audienceType]);

  const recipientCount = getRecipientCount();

  // Validation: can proceed to compose if audience is selected
  const canProceedToCompose = audienceType && (
    audienceType === AudienceType.ALL_USERS ||
    audienceType === AudienceType.ALL_STUDENTS ||
    audienceType === AudienceType.ALL_STAFF ||
    selectedTargets.length > 0
  );

  // Validation: can send if title, body, and valid audience selected
  const canSend = title.trim() !== '' &&
    body.trim() !== '' &&
    canProceedToCompose;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      // Check for audience preference
      const preference = getAudiencePreference();
      if (preference) {
        setActiveTab(preference.activeTab);
        setAudienceType(preference.audienceType);
        setSelectedTargets(preference.selectedTargets);
      } else {
        setActiveTab('STUDENTS');
        setAudienceType('');
        setSelectedTargets([]);
      }
      
      // Check for auto-saved draft
      if (hasAutoDraft()) {
        setShowRestoreBanner(true);
      }
      
      setTitle('');
      setBody('');
      setShowAsPopup(false);
      setShowSuccess(false);
    }
  }, [open]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Enter to send
      if (e.ctrlKey && e.key === 'Enter' && open && !isPending && !showSuccess) {
        e.preventDefault();
        if (canSend) {
          handleSend();
        }
      }
      // Esc to close
      if (e.key === 'Escape' && open && !isPending && !showSuccess) {
        e.preventDefault();
        handleCloseAttempt();
      }
    };

    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, isPending, showSuccess, canSend, hasUnsavedChanges]);

  // Auto-save draft with debounce
  useEffect(() => {
    if (!open) return;

    const hasContent = title.trim() !== '' || body.trim() !== '';
    if (!hasContent) return;

    const timeoutId = setTimeout(() => {
      saveAutoDraft({
        activeTab,
        audienceType,
        selectedTargets,
        title,
        body,
        showAsPopup,
      });
    }, 10000); // 10 seconds

    return () => {
      clearTimeout(timeoutId);
    };
  }, [open, activeTab, audienceType, selectedTargets, title, body, showAsPopup]);

  const handleAudienceTypeSelect = (type: AudienceType) => {
    setAudienceType(type);
    setSelectedTargets([]);
  };



  const handleTemplateClick = (content: string) => {
    setBody(content);
  };

  const handleSend = () => {
    if (!canSend) return;

    // Prepare targetIds based on audience type
    let targetIds: string[] | undefined;
    
    if (selectedTargets.length > 0) {
      if (audienceType === AudienceType.INDIVIDUAL_USERS) {
        // For individual users, send userNames
        if (activeTab === 'STUDENTS') {
          targetIds = selectedTargets.map(id => {
            const student = students.find(s => s.id.toString() === id);
            return student?.userId || id;
          });
        } else if (activeTab === 'EMPLOYEES') {
          targetIds = selectedTargets.map(id => {
            const employee = employees.find(e => e.id?.toString() === id);
            return employee?.userId || id;
          });
        }
      } else {
        // For classes and roles, send IDs as-is
        targetIds = selectedTargets;
      }
    }

    createAnnouncement({
      title,
      body,
      audience: audienceType as AudienceType,
      targetIds,
      showAsPopup,
    }, {
      onSuccess: () => {
        // Save audience preference
        saveAudiencePreference({
          activeTab,
          audienceType,
          selectedTargets,
        });
        
        // Clear auto-draft
        clearAutoDraft();
        
        setShowSuccess(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      },
    });
  };

  const handleCloseAttempt = () => {
    if (hasUnsavedChanges && !showSuccess) {
      setShowDiscardDialog(true);
    } else {
      onClose();
    }
  };

  const handleConfirmDiscard = () => {
    setShowDiscardDialog(false);
    onClose();
  };

  const handleRestoreDraft = () => {
    const draft = getAutoDraft();
    if (draft) {
      setActiveTab(draft.activeTab);
      setAudienceType(draft.audienceType);
      setSelectedTargets(draft.selectedTargets);
      setTitle(draft.title);
      setBody(draft.body);
      setShowAsPopup(draft.showAsPopup);
      setShowRestoreBanner(false);
    }
  };

  const handleClearPreference = () => {
    clearAudiencePreference();
    setActiveTab('STUDENTS');
    setAudienceType('');
    setSelectedTargets([]);
    toast.success('Audience preference cleared');
  };

  const handleOpenSaveDraftDialog = () => {
    const draftsCount = getSavedDrafts().length;
    setDraftName(`Draft ${draftsCount + 1}`);
    setShowSaveDraftDialog(true);
  };

  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    try {
      saveDraft({
        activeTab,
        audienceType,
        selectedTargets,
        title,
        body,
        showAsPopup,
      }, draftName);
      
      toast.success('Draft saved successfully');
      setShowSaveDraftDialog(false);
      onClose();
    } catch (error) {
      toast.error('Failed to save draft');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const formatDraftTime = (isoString: string): string => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const audienceGroups = AUDIENCE_GROUPS[activeTab];
  const targetList = audienceType === AudienceType.CLASS_WISE
    ? classes
    : audienceType === AudienceType.ROLE_WISE
    ? roles
    : audienceType === AudienceType.INDIVIDUAL_USERS
    ? (activeTab === 'STUDENTS' ? students : employees)
    : [];

  const needsTargetSelection =
    audienceType === AudienceType.CLASS_WISE ||
    audienceType === AudienceType.ROLE_WISE ||
    audienceType === AudienceType.INDIVIDUAL_USERS;

  // Success state
  if (showSuccess) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '24px', overflow: 'hidden' },
        }}
      >
        <DialogContent>
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
            }}
          >
            <Box
              sx={{
                width: 96,
                height: 96,
                bgcolor: '#D1FAE5',
                color: '#10B981',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3rem',
                mx: 'auto',
                mb: 3,
                animation: 'zoomIn 0.3s ease',
                '@keyframes zoomIn': {
                  from: { transform: 'scale(0)' },
                  to: { transform: 'scale(1)' },
                },
              }}
            >
              ✓
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
              Announcement Sent!
            </Typography>
            <Typography sx={{ color: '#6B7280', fontSize: '1rem' }}>
              Delivered to {recipientCount} recipients successfully
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseAttempt}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: '1px solid #F3F4F6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                📢 New Announcement
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                Broadcast to students, parents, or staff
              </Typography>
            </Box>
            <IconButton onClick={handleCloseAttempt}>
              <X size={20} />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4 }}>
            {/* Auto-Draft Restore Banner */}
            {showRestoreBanner && (() => {
              const draft = getAutoDraft();
              return draft ? (
                <Box sx={{ bgcolor: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: '12px', p: 2, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#4F46E5' }}>
                        Draft found from {formatDraftTime(draft.updatedAt)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', mt: 0.5 }}>
                        Would you like to restore your previous draft?
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" onClick={handleRestoreDraft}>Restore</Button>
                      <Button size="small" onClick={() => { clearAutoDraft(); setShowRestoreBanner(false); }}>Discard</Button>
                    </Box>
                  </Box>
                </Box>
              ) : null;
            })()}

            {/* Section 1: Audience Selection */}
            <Box sx={{ mb: 4 }}>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: '#9CA3AF',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  mb: 2,
                }}
              >
                Step 1: Choose Audience
              </Typography>

              {/* Tab Switcher */}
              <Box
                sx={{
                  bgcolor: '#F9FAFB',
                  p: 0.5,
                  borderRadius: '16px',
                  display: 'inline-flex',
                  mb: 3,
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(_, val) => {
                    setActiveTab(val);
                    setAudienceType('');
                    setSelectedTargets([]);
                  }}
                  sx={{
                    minHeight: 40,
                    '& .MuiTabs-indicator': { display: 'none' },
                  }}
                >
                  <Tab
                    label="Everyone"
                    value="EVERYONE"
                    icon={<span>🌐</span>}
                    iconPosition="start"
                    sx={{
                      minHeight: 40,
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      textTransform: 'none',
                      borderRadius: '12px',
                      px: 3,
                      '&.Mui-selected': {
                        bgcolor: '#F59E0B',
                        color: 'white',
                      },
                    }}
                  />
                  <Tab
                    label="Students & Parents"
                    value="STUDENTS"
                    icon={<span>👨‍🎓</span>}
                    iconPosition="start"
                    sx={{
                      minHeight: 40,
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      textTransform: 'none',
                      borderRadius: '12px',
                      px: 3,
                      '&.Mui-selected': {
                        bgcolor: '#6366F1',
                        color: 'white',
                      },
                    }}
                  />
                  <Tab
                    label="Staff Members"
                    value="EMPLOYEES"
                    icon={<span>👥</span>}
                    iconPosition="start"
                    sx={{
                      minHeight: 40,
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      textTransform: 'none',
                      borderRadius: '12px',
                      px: 3,
                      '&.Mui-selected': {
                        bgcolor: '#6366F1',
                        color: 'white',
                      },
                    }}
                  />
                </Tabs>
              </Box>

              {/* Audience Type Pills */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                {audienceGroups.map((group) => (
                  <Box
                    key={group.id}
                    onClick={() => handleAudienceTypeSelect(group.type)}
                    sx={{
                      flex: 1,
                      p: 3,
                      bgcolor: audienceType === group.type ? '#EEF2FF' : 'white',
                      border: `2px solid ${audienceType === group.type ? '#6366F1' : '#E5E7EB'}`,
                      borderRadius: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#6366F1',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Box sx={{ fontSize: '2rem', mb: 1 }}>{group.icon}</Box>
                    <Typography sx={{ fontWeight: 900, fontSize: '1rem' }}>
                      {group.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
                      {group.desc}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Clear Preference Button */}
              {getAudiencePreference() && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button
                    size="small"
                    onClick={handleClearPreference}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      color: '#6B7280',
                      '&:hover': {
                        bgcolor: '#F3F4F6',
                      },
                    }}
                  >
                    Clear preference
                  </Button>
                </Box>
              )}

              {/* Target Selection (if needed) */}
              {needsTargetSelection && (
                <Box
                  sx={{
                    mt: 2,
                    p: 3,
                    bgcolor: '#F9FAFB',
                    borderRadius: '16px',
                  }}
                >
                  <Autocomplete
                    multiple
                    fullWidth
                    options={
                      audienceType === AudienceType.CLASS_WISE
                        ? classes
                        : audienceType === AudienceType.ROLE_WISE
                        ? roles
                        : activeTab === 'STUDENTS'
                        ? students
                        : employees
                    }
                    value={
                      audienceType === AudienceType.CLASS_WISE
                        ? classes.filter(c => selectedTargets.includes(c.id.toString()))
                        : audienceType === AudienceType.ROLE_WISE
                        ? roles.filter(r => selectedTargets.includes(r.id.toString()))
                        : activeTab === 'STUDENTS'
                        ? students.filter(s => selectedTargets.includes(s.id.toString()))
                        : employees.filter(e => e.id && selectedTargets.includes(e.id.toString()))
                    }
                    onChange={(event, newValue) => {
                      const ids = newValue.map(item => item.id.toString());
                      setSelectedTargets(ids);
                    }}
                    getOptionLabel={(option) => {
                      if (audienceType === AudienceType.CLASS_WISE) {
                        return option.displayName || '';
                      } else if (audienceType === AudienceType.ROLE_WISE) {
                        return option.name || '';
                      } else if (activeTab === 'STUDENTS') {
                        return `${[option.firstName, option.lastName].filter(Boolean).join(' ')} (${option.studentId})`;
                      } else {
                        return `${[option.firstName, option.lastName].filter(Boolean).join(' ')} (${option.employeeId})`;
                      }
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`Select ${
                          audienceType === AudienceType.CLASS_WISE
                            ? 'Classes'
                            : audienceType === AudienceType.ROLE_WISE
                            ? 'Roles'
                            : activeTab === 'STUDENTS'
                            ? 'Students'
                            : 'Employees'
                        }`}
                        placeholder="Search and select..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#E5E7EB',
                            },
                          },
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const label = audienceType === AudienceType.CLASS_WISE
                          ? option.displayName
                          : audienceType === AudienceType.ROLE_WISE
                          ? option.name
                          : activeTab === 'STUDENTS'
                          ? `${[option.firstName, option.lastName].filter(Boolean).join(' ')} (${option.studentId})`
                          : `${[option.firstName, option.lastName].filter(Boolean).join(' ')} (${option.employeeId})`;
                        return (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={label}
                            size="small"
                            sx={{
                              bgcolor: '#6366F1',
                              color: 'white',
                              fontWeight: 700,
                              '& .MuiChip-deleteIcon': {
                                color: 'rgba(255, 255, 255, 0.7)',
                                '&:hover': {
                                  color: 'white',
                                },
                              },
                            }}
                          />
                        );
                      })
                    }
                    sx={{
                      '& .MuiAutocomplete-tag': {
                        margin: '2px',
                      },
                    }}
                  />
                  {selectedTargets.length > 0 && (
                    <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', mt: 2 }}>
                      {selectedTargets.length} selected
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            {/* Section 2: Compose (appears after audience selected) */}
            {canProceedToCompose && (
              <Box
                sx={{
                  animation: 'slideIn 0.3s ease',
                  '@keyframes slideIn': {
                    from: { opacity: 0, transform: 'translateY(10px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#9CA3AF',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    mb: 2,
                  }}
                >
                  Step 2: Compose Message
                </Typography>

                <TextField
                  fullWidth
                  placeholder="e.g., 🎉 Annual Day Tomorrow!"
                  value={title}
                  onChange={(e) => setTitle(e.target.value.slice(0, VALIDATION_RULES.ANNOUNCEMENT_TITLE_MAX_LENGTH))}
                  error={title.trim() === '' && (body.trim() !== '' || audienceType !== '')}
                  helperText={title.trim() === '' && (body.trim() !== '' || audienceType !== '') ? 'Title is required' : ''}
                  sx={{
                    mb: 2,
                    '& .MuiInputBase-root': {
                      fontSize: '2rem',
                      fontWeight: 900,
                      border: 'none',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                />
                <Typography
                  sx={{
                    textAlign: 'right',
                    fontSize: '0.75rem',
                    color: '#9CA3AF',
                    mb: 3,
                  }}
                >
                  {title.length}/{VALIDATION_RULES.ANNOUNCEMENT_TITLE_MAX_LENGTH}
                </Typography>

                {/* Quick Templates */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Sparkles size={16} color="#6366F1" />
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#6366F1' }}>
                      Quick Templates
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      overflowX: 'auto',
                      pb: 1,
                      '&::-webkit-scrollbar': {
                        height: 6,
                      },
                      '&::-webkit-scrollbar-thumb': {
                        bgcolor: '#E5E7EB',
                        borderRadius: '3px',
                      },
                    }}
                  >
                    {ANNOUNCEMENT_TEMPLATES.map((template, idx) => (
                      <Chip
                        key={idx}
                        label={template.label}
                        onClick={() => handleTemplateClick(template.content)}
                        sx={{
                          bgcolor: 'white',
                          border: '1.5px solid #E5E7EB',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: '#EEF2FF',
                            borderColor: '#6366F1',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  placeholder="Type your message here..."
                  value={body}
                  onChange={(e) => setBody(e.target.value.slice(0, VALIDATION_RULES.ANNOUNCEMENT_BODY_MAX_LENGTH))}
                  error={body.trim() === '' && (title.trim() !== '' || audienceType !== '')}
                  helperText={body.trim() === '' && (title.trim() !== '' || audienceType !== '') ? 'Message is required' : ''}
                  sx={{
                    mb: 1,
                    '& .MuiInputBase-root': {
                      bgcolor: '#F9FAFB',
                      borderRadius: '16px',
                      fontSize: '1rem',
                      p: 3,
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: '#9CA3AF',
                    mb: 3,
                  }}
                >
                  {body.length}/{VALIDATION_RULES.ANNOUNCEMENT_BODY_MAX_LENGTH} characters
                </Typography>

                <EmergencyPopupToggle
                  checked={showAsPopup}
                  onChange={setShowAsPopup}
                />
              </Box>
            )}
          </Box>

          {/* Footer */}
          {canProceedToCompose && (
            <Box
              sx={{
                p: 3,
                borderTop: '1px solid #F3F4F6',
                display: 'flex',
                gap: 2,
                alignItems: 'center',
              }}
            >
              <Button
                variant="text"
                onClick={handleOpenSaveDraftDialog}
                startIcon={<Save size={16} />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#6B7280',
                  '&:hover': {
                    bgcolor: '#F3F4F6',
                  },
                }}
              >
                Save Draft
              </Button>
              {showAsPopup && (
                <Button
                  variant="outlined"
                  onClick={() => setShowPreview(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: '12px',
                    px: 3,
                  }}
                >
                  Preview Popup
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={!canSend || isPending}
                startIcon={isPending ? <CircularProgress size={20} /> : <Send size={18} />}
                sx={{
                  bgcolor: '#6366F1',
                  textTransform: 'none',
                  fontWeight: 900,
                  fontSize: '1rem',
                  py: 1.5,
                  px: 4,
                  borderRadius: '12px',
                  '&:hover': {
                    bgcolor: '#4F46E5',
                  },
                  '&:disabled': {
                    bgcolor: '#E5E7EB',
                    color: '#9CA3AF',
                  },
                }}
              >
                {isPending ? 'Sending...' : 'Send Announcement'}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Popup Preview Modal */}
      {showPreview && (
        <PopupPreview
          title={title}
          body={body}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Discard Changes Dialog */}
      <Dialog
        open={showDiscardDialog}
        onClose={() => setShowDiscardDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px' },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Discard changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to discard them?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setShowDiscardDialog(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#6B7280',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDiscard}
            variant="contained"
            color="error"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Draft Dialog */}
      <Dialog
        open={showSaveDraftDialog}
        onClose={() => setShowSaveDraftDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px' },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Save Draft</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Give your draft a name (optional)
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder="Draft name"
            sx={{
              '& .MuiInputBase-root': {
                borderRadius: '12px',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setShowSaveDraftDialog(false)}
            disabled={isSavingDraft}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#6B7280',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveDraft}
            variant="contained"
            disabled={isSavingDraft}
            startIcon={isSavingDraft ? <CircularProgress size={16} /> : <Save size={16} />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              bgcolor: '#6366F1',
              '&:hover': {
                bgcolor: '#4F46E5',
              },
            }}
          >
            {isSavingDraft ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
